import { alpha } from '@mui/material/styles';

// ----------------------------------------------------------------------

export type ColorSchema = 'primary' | 'secondary' | 'info' | 'success' | 'warning' | 'error';

declare module '@mui/material/styles/createPalette' {
  interface TypeBackground {
    neutral: string;
  }
  interface SimplePaletteColorOptions {
    lighter: string;
    darker: string;
  }
  interface PaletteColor {
    lighter: string;
    darker: string;
  }
}

// SETUP COLORS
export const Colors = {
  midGreyText: "#70787D",
  lightGreyText: "#a6aba8",
  greyText: "#010E07",
  topbarSidebar: "white",
  lightGreen: "#C3F53C",
  darkGreen: "#31bb3f",
  red: "#F5523C",
  white: "#FFFFFF",
  tableBorderColor: "#010e071a",
  tableHeaderColor: "#F3F5F4",
  brandPrimary: "#F3F5F4",
  brandSecondary: "#000000",
}

const GREY = {
  0: '#FFFFFF',
  100: '#F9FAFB',
  200: '#F4F6F8',
  300: '#DFE3E8',
  400: '#C4CDD5',
  500: '#919EAB',
  600: '#637381',
  700: '#454F5B',
  800: '#212B36',
  900: '#161C24',
};

const PRIMARY = {
  lighter: '#C8FAD6',
  light: '#5BE49B',
  main: Colors.brandPrimary,
  dark: Colors.brandSecondary,
  darker: '#004B50',
  contrastText: '#000000',
};

const SECONDARY = {
  lighter: Colors.greyText,
  light: Colors.greyText,
  main: Colors.greyText,
  dark: alpha(Colors.greyText, 0.95),
  darker: Colors.greyText,
  contrastText: Colors.white,
};

const INFO = {
  lighter: '#CAFDF5',
  light: '#61F3F3',
  main: '#2196F3',
  dark: '#006C9C',
  darker: '#003768',
  contrastText: Colors.white,
};

const SUCCESS = {
  lighter: '#D3FCD2',
  light: '#77ED8B',
  main: Colors.darkGreen,
  dark: '#118D57',
  darker: '#065E49',
  contrastText: Colors.white,
};

const WARNING = {
  lighter: Colors.red,
  light: Colors.red,
  main: Colors.red,
  dark: Colors.red,
  darker: Colors.red,
  contrastText: Colors.white,
};

const ERROR = {
  lighter: '#FFE9D5',
  light: '#FFAC82',
  main: '#FF5630',
  dark: '#B71D18',
  darker: '#7A0916',
  contrastText: '#FFFFFF',
};

const COMMON = {
  common: {
    black: '#000000',
    white: '#FFFFFF',
  },
  primary: PRIMARY,
  secondary: SECONDARY,
  info: INFO,
  success: SUCCESS,
  warning: WARNING,
  error: ERROR,
  grey: GREY,
  divider: alpha(GREY[500], 0.2),
  action: {
    hover: alpha(GREY[500], 0.1),
    selected: alpha(GREY[500], 0.16),
    disabled: alpha(GREY[500], 0.8),
    disabledBackground: alpha(GREY[500], 0.24),
    focus: alpha(GREY[500], 0.24),
    hoverOpacity: 0.08,
    disabledOpacity: 0.48,
  },
};

export function palette(mode: 'light' | 'dark') {
  const light = {
    ...COMMON,
    mode: 'light',
    text: {
      primary: Colors.greyText,
      secondary: Colors.greyText,
      disabled: GREY[800],
    },
    background: {
      paper: '#FFFFFF',
      default: '#FFFFFF',
      neutral: Colors.tableHeaderColor,
    },
    action: {
      ...COMMON.action,
      active: GREY[600],
    },
  };

  const dark = {
    ...COMMON,
    mode: 'dark',
    text: {
      primary: '#FFFFFF',
      secondary: GREY[500],
      disabled: GREY[600],
    },
    background: {
      paper: GREY[800],
      default: GREY[900],
      neutral: alpha(GREY[500], 0.12),
    },
    action: {
      ...COMMON.action,
      active: GREY[500],
    },
  };

  return mode === 'light' ? light : dark;
}
